import React, { useState, useEffect } from 'react';
import useStyles from './styles';
import { get, size, set } from 'global/lib/lodash';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { FormControl, InputLabel, MenuItem, Select, Button, TableContainer, TableHead, TableBody, TableRow, TableCell, Checkbox, Table, IconButton } from '@mui/material';
import clsx from 'clsx';
import { stopProp } from 'global/utils';
import { getRealmIcon } from 'global/components/droprealms';
import withRouter from 'react-router-dom/withRouter';
import sendData from 'redux/actions/sendData';
import { Loader, useScreen } from 'global/styles';
import CloseIcon from '@mui/icons-material/Close';
import Alert from '@mui/material/Alert';
import { useSelector } from 'react-redux';

export default withRouter((props) => {
  const { isMobile } = useScreen();
  const classes = useStyles({ isMobile });
  const [error, setError] = useState();
  const [state, setState] = useState({
    scorecards: [],
    scValue: 0,
    status: [
      { title: 'Default', value: 'template' },
      { title: 'Global', value: 'global_template' },
    ],
    statusValue: 0,
    selected: {},
    loading: false,
  });
  const realm = useSelector((state) => get(state, 'auth.realm.id'));
  const templates = useRetrieveData(
    { url: '/assessment-templates' },
    {
      build: ['assessments'],
      useEffect: true,
      params: {
        include: 'scorecards,realm,authors',
        filter: realm && { realm },
      },
    }
  );

  const temps = get(templates, 'data', []);

  useEffect(() => {
    if (size(temps)) {
      const scorecards = {};
      temps.map((template) => {
        template.scorecards.map((sc) => {
          set(scorecards, get(sc, 'id'), sc);
        });
      });

      setState((s) => ({
        ...s,
        scorecards: Object.keys(scorecards).map((k) => ({ ...scorecards[k] })),
      }));
    }
  }, [temps]);

  const data = temps.filter((t) => {
    let passes = true;
    if (state.scValue) {
      passes = get(t, 'scorecards', []).find((sc) => sc.id === state.scValue);
    }

    if (passes && state.statusValue) {
      passes = get(t, 'status', '') === state.statusValue;
    }

    return passes;
  });

  const checkChange = (row, e) => {
    stopProp(e);
    const selected = { ...state.selected };
    const isSel = !get(selected, row.id);
    set(selected, row.id, isSel);
    setState((s) => ({ ...s, selected }));
  };

  const selectRow = (row) => {
    props.history.push(`/reports/assessment-template/${row.id}`, { from: 'account/content' });
  };

  const processSelected = async (status = 'template') => {
    try {
      setState((s) => ({ ...s, loading: true }));
      await Promise.all(
        Object.keys(state.selected)
          .filter((k) => state.selected[k])
          .map(async (k) => {
            const item = data.find((d) => d.id === k);
            if (item) {
              const id = get(item, 'id').toString();
              await sendData({
                method: 'patch',
                url: `/assessment-templates/${id}`,
                data: {
                  data: {
                    type: 'assessment-templates',
                    id,
                    attributes: {
                      status,
                    },
                  },
                },
              });
            }
          })
      );
      await templates.update();
    } catch (err) {
      setError(`Failed to update: ${err.message}`);
    } finally {
      setState((s) => ({ ...s, loading: false }));
    }
  };

  const hasSelected = size(Object.keys(state.selected).filter((k) => !!state.selected[k]));

  return state.loading ? (
    <div className={clsx(classes.container, classes.flexRow, classes.pad20)}>
      <Loader />
    </div>
  ) : (
    <div className={classes.container}>
      {error && (
        <Alert
          severity="error"
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => {
                setError();
              }}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          {error}
        </Alert>
      )}

      <div className={classes.flexRow}>
        <FormControl className={classes.formControl}>
          <InputLabel>Scorecard</InputLabel>
          <Select value={state.scValue} onChange={(e) => setState((s) => ({ ...s, scValue: e.target.value }))}>
            <MenuItem value={0}>Any</MenuItem>
            {state.scorecards.map((sc) => (
              <MenuItem key={get(sc, 'id')} value={get(sc, 'id')}>
                {get(sc, 'name')}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl className={classes.formControl}>
          <InputLabel>Status</InputLabel>
          <Select value={state.statusValue} onChange={(e) => setState((s) => ({ ...s, statusValue: e.target.value }))}>
            <MenuItem value={0}>Any</MenuItem>
            {state.status.map((sc) => (
              <MenuItem key={sc.value} value={sc.value}>
                {sc.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      {!!hasSelected && (
        <div className={clsx(classes.flexRow, classes.buttonRow)}>
          <Button color="secondary" variant="contained" onClick={() => processSelected('global_template')}>
            MAKE GLOBAL
          </Button>
          <Button color="secondary" variant="contained" onClick={() => processSelected('template')}>
            REMOVE FROM GLOBAL
          </Button>
        </div>
      )}

      <div className={classes.table}>
        {templates.loading && (
          <div className={clsx(classes.flexRow, classes.pad20)}>
            <Loader />
          </div>
        )}
        {!templates.loading && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox"></TableCell>
                  {COLUMNS.map((col) => (
                    <TableCell key={col.field} size="small" width={col.width}>
                      {col.title}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((temp) => {
                  return (
                    <TableRow key={temp.id} hover onClick={() => selectRow(temp)}>
                      <TableCell padding="checkbox">
                        <Checkbox checked={!!get(state, `selected.${temp.id}`)} onClick={(e) => checkChange(temp, e)} />
                      </TableCell>
                      {COLUMNS.map((col) => {
                        const val = col.render ? col.render(temp, col) : get(temp, col.field);
                        return (
                          <TableCell key={col.field} size={'small'} width={col.width}>
                            {val}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </div>
    </div>
  );
});

const COLUMNS = [
  { field: 'name', title: 'Title', width: 200 },
  {
    field: 'realm.name',
    title: 'Location',
    width: 200,
    render: (row) => {
      const Rcon = getRealmIcon(row.realm);
      return (
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <div style={{ marginRight: 5 }}>{Rcon}</div>
          {get(row, 'realm.name')}
        </div>
      );
    },
  },
  { field: 'authors.0.name', title: 'Author', width: 150 },
  {
    field: 'status',
    title: 'Global',
    width: 100,
    render: (row, props) => {
      return row.status === 'global_template' ? 'Yes' : 'No';
    },
  },
];
